import { useEffect, useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';

// ===== Brand tokens (matched to HomePage) =====
const BLUE = '#286BCD';
const DARK = '#1a1a1a';
const MUTED = '#6b6b6b';
const SERIF = "'Cormorant Garamond', Garamond, serif";
const SANS = "'Jost', sans-serif";

// Hide the live count until enough people are in line for the number to read
// as momentum rather than emptiness.
const COUNT_DISPLAY_THRESHOLD = 100;

type FoundingMembersBarProps = {
  /** Optional controlled count. When provided, overrides the value fetched on mount. */
  count?: number;
};

const FoundingMembersBar: React.FC<FoundingMembersBarProps> = ({
  count: controlledCount,
}) => {
  const [fetched, setFetched] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .rpc('get_waitlist_count');
      if (!active || error) return;
      const n = typeof data === 'number' ? data : Number(data);
      if (Number.isFinite(n)) setFetched(n);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Controlled count (e.g. just after a join) wins over the mount fetch.
  const count = controlledCount ?? fetched;
  const showCount = count != null && count >= COUNT_DISPLAY_THRESHOLD;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
      {/* Live count — hidden below COUNT_DISPLAY_THRESHOLD */}
      {showCount && (
        <div
          style={{
            fontFamily: SERIF,
            fontWeight: 300,
            fontSize: 'clamp(28px, 4vw, 40px)',
            color: DARK,
            lineHeight: 1.1,
            marginBottom: 4,
          }}
        >
          {count.toLocaleString()}{' '}
          <span style={{ color: BLUE }}>in line</span>
        </div>
      )}

      {/* Supporting copy */}
      <p
        style={{
          fontFamily: SANS,
          fontSize: 15,
          lineHeight: 1.6,
          color: MUTED,
          marginTop: 20,
        }}
      >
        Joining is free. You'll hear about it here first.
      </p>
    </div>
  );
};

export default FoundingMembersBar;
